import React from 'react';
import { ArrowRightIcon } from 'lucide-react';
import { StoryHeadline, Reveal, StoryPhoto, usePick, bodyText } from './StoryShared';
import { AtnaConvergence } from './StoryVisuals';
import { act3, act4, storyImages } from '../../data/storyPage';

/* Act 3 (listening + problems as gifts) + Act 4 (technology catches up) */
export function StoryLearning() {
  const pick = usePick();
  return <>
      {/* Act 3 — listening */}
      <section className="px-6 py-20 lg:py-28" aria-labelledby="story-listen" data-id="element-6647" data-mp="2rwz2pdfogz05">
        <div className="max-w-6xl mx-auto grid grid-cols-1 lg:grid-cols-[1fr_1.15fr] gap-10 lg:gap-16 items-center" data-id="element-6648" data-mp="2wviqljdu0kv5">
          <Reveal data-id="element-6649" data-mp="30yotch1qjtwv">
            <StoryHeadline id="story-listen" size="lg" lines={act3.headline} data-id="element-6650" data-mp="30w2legaxnce4" />
            <p className={`mt-6 max-w-[480px] ${bodyText}`} data-id="element-6651" data-mp="35wqc10v91r7t">{pick(act3.body)}</p>
            <p className={`mt-4 max-w-[480px] ${bodyText}`} data-id="element-6652" data-mp="16l7ctdzqzxbc">{pick(act3.role)}</p>
            <ul className="mt-4 space-y-1" data-id="element-6653" data-mp="1bipecjbfra9b">
              {act3.roleSteps.map((s, i) => <li key={s.fr} className={`font-sans text-[16px] md:text-[17px] font-medium ${i === act3.roleSteps.length - 1 ? 'text-[#4F46E5]' : 'text-[#121A3A]'}`} data-id="element-6654" data-mp="xs6kg9ijb6jv" data-mpi={i}>
                  {pick(s)}
                </li>)}
            </ul>
          </Reveal>
          <Reveal delay={0.05} data-id="element-6655" data-mp="ox8lakosmxk3">
            <StoryPhoto src={storyImages.listening} alt={pick(act3.imageAlt)} data-id="element-6656" data-mp="k0s09zcw4zb8" />
          </Reveal>
        </div>

        <Reveal className="max-w-6xl mx-auto mt-16 lg:mt-24" data-id="element-6657" data-mp="1gqso9w5frbrh">
          <StoryHeadline as="p" size="xl" lines={act3.statement} data-id="element-6658" data-mp="1go6ctlffcc3z" />
        </Reveal>

        {/* Conclusion of the listening act: every problem is a gift */}
        <div className="max-w-6xl mx-auto mt-16 lg:mt-24 pt-14 lg:pt-16 border-t border-[#ECEDF2] grid grid-cols-1 lg:grid-cols-[0.8fr_1.2fr] gap-10 lg:gap-16" data-id="element-6659" data-mp="17ojamxevf29v">
          <Reveal data-id="element-6660" data-mp="3sgl5hi1ruocz">
            <StoryHeadline size="md" as="h3" lines={act3.giftHeadline} data-id="element-6661" data-mp="3sh3ab32i2awi" />
            <p className={`mt-5 max-w-[400px] ${bodyText}`} data-id="element-6662" data-mp="3nh8huv32td9c">{pick(act3.giftBody)}</p>
          </Reveal>
          <Reveal delay={0.05} data-id="element-6663" data-mp="3oye00iy4npw8">
            <p className="font-serif text-[1.9rem] sm:text-[2.3rem] lg:text-[2.7rem] leading-[1.14] tracking-tight" data-id="element-6664" data-mp="3ozflzigolwsq">
              {act3.giftLines.map(l => <span key={l.text.fr} className={`block ${l.accent ? 'text-[#4F46E5]' : 'text-[#121A3A]'}`} data-id="element-6665" data-mp="3fxps85zbulrs">{pick(l.text)}</span>)}
            </p>
            <p className="mt-6 font-sans text-[13px] italic text-[#8A8FA3]" data-id="element-6666" data-mp="3kups1z5l2sag">{pick(act3.attribution)}</p>
          </Reveal>
        </div>
      </section>

      {/* Act 4 — technology catches up */}
      <section className="relative overflow-hidden px-6 py-20 lg:py-28 bg-[#F8F7FF] border-y border-[#EEEDFB]" aria-labelledby="story-tech" data-id="element-6667" data-mp="3azo1qzzknk6a">
        <div className="pointer-events-none absolute left-1/2 top-1/3 -translate-x-1/2 w-[760px] h-[420px] rounded-full bg-[#ECEAFD] opacity-70 blur-[140px]" aria-hidden="true" data-id="element-6668" data-mp="318a4kyv3ewgb" />
        <div className="relative max-w-5xl mx-auto" data-id="element-6669" data-mp="2nb4tulkfp9eq">
          <Reveal className="text-center" data-id="element-6670" data-mp="2s6k473j8gn6e">
            <StoryHeadline id="story-tech" size="lg" lines={act4.headline} data-id="element-6671" data-mp="2s9pswhny9n1l" />
          </Reveal>
          <Reveal delay={0.05} className="mt-12 lg:mt-16" data-id="element-6672" data-mp="28nwnby22ls93">
            <AtnaConvergence data-id="element-6673" data-mp="23oa8xlaia5yp" />
          </Reveal>
          <Reveal className="mt-16 lg:mt-20 text-center" data-id="element-6674" data-mp="2ik9hex1j284d">
            <StoryHeadline as="p" size="xl" lines={act4.born} data-id="element-6675" data-mp="2ilu98ewgjcyj" />
            <p className="mt-8 font-sans text-[16px] md:text-[17px] text-[#5A6178]" data-id="element-6676" data-mp="2dkmzwbeo25pr">{pick(act4.notJust)}</p>
            <ol className="mt-4 flex flex-wrap items-center justify-center gap-x-3 gap-y-2" data-id="element-6677" data-mp="2rk610k928gsk">
              {act4.verbs.map((v, i) => <li key={v.fr} className="flex items-center gap-3" data-id="element-6678" data-mp="2mlkyge6c5l0y" data-mpi={i}>
                  <span className={`font-sans text-[13px] sm:text-[14px] font-semibold tracking-[0.16em] uppercase ${i === act4.verbs.length - 1 ? 'text-[#4F46E5]' : 'text-[#121A3A]'}`} data-id="element-6679" data-mp="319phxkd7q0ks">
                    {pick(v)}
                  </span>
                  {i < act4.verbs.length - 1 && <ArrowRightIcon className="w-4 h-4 text-[#A5A0F3]" aria-hidden="true" data-id="element-6680" data-mp="36adj2yv5nxsq" />}
                </li>)}
            </ol>
          </Reveal>
        </div>
      </section>
    </>;
}